import { ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toTotpCode } from '../api/mappers';
import { useDisableTwoFactor } from '../hooks/use-two-factor-mutations';
import { twoFactorFieldErrors } from '../schemas/two-factor-rules';
import { disableTwoFactorSchema, type DisableTwoFactorValues } from '../schemas/two-factor-schemas';

import { OneTimeCodeInput } from './one-time-code-input';

const DEFAULTS: DisableTwoFactorValues = { password: '', code: '' };

/** Two-factor off needs both the password and a live authenticator code. */
export function TurnOffTwoFactorDialog() {
  const { t } = useTranslation('auth');
  const message = useErrorMessage();
  const disable = useDisableTwoFactor();
  const [open, setOpen] = useState(false);
  const form = useForm<DisableTwoFactorValues>({
    resolver: useI18nResolver(disableTwoFactorSchema),
    defaultValues: DEFAULTS,
  });

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      form.reset(DEFAULTS);
      disable.reset();
    }
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await disable.mutateAsync({ password: values.password, code: toTotpCode(values.code) });
      onOpenChange(false);
    } catch (error) {
      const fields = twoFactorFieldErrors(error);
      if (fields.includes('password')) {
        form.setError('password', { type: 'server', message: t('disable.wrongPassword') });
      }
      if (fields.includes('code')) {
        form.setError('code', { type: 'server', message: t('setup.codeMismatch') });
      }
      if (fields.length === 0) {
        const requiredByPlatform =
          error instanceof ApiError && error.code === 'TWO_FACTOR_REQUIRED_BY_ORGANIZATION';
        form.setError('root.server', {
          type: 'server',
          message: requiredByPlatform ? t('disable.requiredByPlatform') : message(error),
        });
      }
    }
  });

  const rootError = form.formState.errors.root?.server?.message;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="text-destructive hover:text-destructive">
          <ShieldOff aria-hidden />
          {t('security.turnOff')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('disable.title')}</DialogTitle>
          <DialogDescription>{t('disable.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {rootError ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{rootError}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.password')}</FormLabel>
                  <FormControl>
                    <PasswordInput autoComplete="current-password" autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('twoFactor.label.totp')}</FormLabel>
                  <FormControl>
                    <OneTimeCodeInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" variant="destructive" disabled={disable.isPending}>
                {disable.isPending ? t('disable.submitting') : t('disable.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
